import {
    add,
    differenceInYears,
    format as dateFnsFormat,
    parse,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { z } from 'zod';

export const DEFAULT_DATE_FORMAT = 'MM/dd/yyyy';
export const DEFAULT_DATE_FORMAT_DASH = 'MM-dd-yyyy';
export const SHORT_MONTH_DATE_FORMAT_SLASH = 'MM/dd/yy';
export const ISO_DATE_ONLY_FORMAT = 'yyyy-MM-dd';
export const SHORT_READABLE_DATE_FORMAT = 'MMM dd, yyyy';

export const TIME_FORMAT = 'h:mm aaa';
export const TIME_FORMAT_WITH_TIMEZONE = 'h:mma z';

export const AMBIGUOUS_DATE_FORMAT = 'MM/dd/yyyy';
export const DEFAULT_DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/; // MM/DD/YYYY

export const convertToDate = (date: Date | string) =>
    date instanceof Date
        ? date
        : DEFAULT_DATE_REGEX.exec(date)
          ? parse(date, DEFAULT_DATE_FORMAT, new Date()) // Specifically parse potentially ambiguous date format
          : new Date(date);

export const format = (date: Date | string, dateFormat?: string): string => {
    const normalized = convertToDate(date);
    return dateFnsFormat(normalized, dateFormat ?? DEFAULT_DATE_FORMAT);
};

/**
 * Formats when the value is non-empty and parses to a valid date; otherwise
 * returns `fallback` (default empty string).
 */
export const formatOptional = (
    date: Date | string | null | undefined,
    dateFormat: string,
    fallback = '',
): string => {
    if (date == null || date === '') {
        return fallback;
    }
    const parsed = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(parsed.getTime())) {
        return fallback;
    }
    return format(date, dateFormat);
};

export const formatWithoutTimezone = (
    date: Date | string,
    dateFormat?: string,
): string => {
    const normalized = convertToDate(date);

    // strips 'Z' from date to remove timezone
    return dateFnsFormat(
        new Date(normalized.toISOString().slice(0, -1)),
        dateFormat ?? DEFAULT_DATE_FORMAT,
    );
};

export const formatInTimezone = (date: Date | string, dateFormat: string) => {
    const normalized = convertToDate(date);

    return formatInTimeZone(
        normalized,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        dateFormat,
    );
};

export const formatDateTime = (date: Date | string) => {
    const normalized = convertToDate(date);
    return dateFnsFormat(normalized, 'MM/dd/yyyy hh:mmaaa');
};

export const formatTimeDate = (date: Date | string) => {
    const normalized = convertToDate(date);
    return dateFnsFormat(normalized, 'h:mmaaa MMM dd, yyyy');
};

export const formatReadableMonthDay = (date: Date | string) => {
    const normalized = convertToDate(date);
    return dateFnsFormat(normalized, 'MMMM do');
};

export const formatDayOfWeekMonthDay = (date: Date | string) => {
    const normalized = convertToDate(date);
    return dateFnsFormat(normalized, 'EEEE, MMMM do');
};

const getTimeZoneLabelFromParts = (
    timeZone: string,
    timeZoneName: Intl.DateTimeFormatOptions['timeZoneName'],
): string | undefined => {
    try {
        const part = new Intl.DateTimeFormat(undefined, {
            timeZone,
            timeZoneName,
        })
            .formatToParts(new Date())
            .find(({ type }) => type === 'timeZoneName');
        return part?.value;
    } catch {
        return undefined;
    }
};

/** Human-readable timezone label, e.g. "eastern time" (not "America/New_York"). */
export const getTimeZoneLabel = (
    timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string => {
    const genericName = getTimeZoneLabelFromParts(timeZone, 'longGeneric');
    if (genericName != null && genericName.length > 0) {
        return genericName;
    }

    const longName = getTimeZoneLabelFromParts(timeZone, 'long');
    if (longName != null && longName.length > 0) {
        return longName.replace(/\s(standard|daylight|summer)\s/i, ' ').trim();
    }

    return timeZone.replace(/_/g, ' ');
};

/** Human-readable client timezone, e.g. "eastern time" (not "America/New_York"). */
export const getClientTimeZoneLabel = (): string => getTimeZoneLabel();

export const formatInSpecificTimeZone = (
    date: Date | string,
    timeZone: string,
    dateFormat: string,
): string => {
    const normalized = convertToDate(date);
    return formatInTimeZone(normalized, timeZone, dateFormat);
};

export const formatUTCDateToISODateString = (date: Date): string => {
    return formatInTimeZone(date, 'UTC', ISO_DATE_ONLY_FORMAT);
};

export const diffInYearsFromNow = (date: Date | string): number => {
    const normalized = convertToDate(date);
    return differenceInYears(new Date(), normalized);
};

export const tomorrow = (): Date => {
    return add(new Date(), { days: 1 });
};

export const isoDateToLocalTimezoneDate = (isoDate: string): Date => {
    return new Date(`${isoDate}T00:00:00`); // Z missing so date interpreted in browser timezone
};

export const isoDateToUTCTimezoneDate = (isoDate: string): Date => {
    return new Date(`${isoDate}T00:00:00Z`);
};

export const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th'; // Covers 11th, 12th, 13th
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

export const formatDateToOrdinal = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix = getOrdinalSuffix(day);
    return `${day}${suffix}`;
};

export const formatAsMonthDayYearUTC = (date: Date): string => {
    return formatInTimeZone(date, 'UTC', 'MMM d, yyyy');
};

/** Adds whole UTC years. A Feb 29 anniversary lands on Mar 1 in a non-leap year. */
export const addUtcYears = (date: Date, years: number): Date => {
    const next = new Date(date.getTime());
    next.setUTCFullYear(next.getUTCFullYear() + years);
    return next;
};

/**
 * The occurrence of `anniversary` that exclusively ends the year containing
 * `now`. Whole UTC years are added or removed until `now` sits in
 * `[end - 1 year, end)`. An anniversary equal to `now` starts the next year,
 * so the result is strictly after `now`. `maxYears` caps each direction.
 */
export const resolveNextAnniversary = (
    anniversary: Date,
    now: Date,
    maxYears: number,
): Date => {
    let end = new Date(anniversary.getTime());
    let guard = 0;
    while (end.getTime() <= now.getTime() && guard < maxYears) {
        end = addUtcYears(end, 1);
        guard += 1;
    }
    guard = 0;
    while (addUtcYears(end, -1).getTime() > now.getTime() && guard < maxYears) {
        end = addUtcYears(end, -1);
        guard += 1;
    }
    return end;
};

/**
 * Whole UTC years from the year that ends at `anniversary`.
 * `0` is `[anniversary - 1 year, anniversary)`.
 * An instant equal to `anniversary` is `1`.
 * The result is clamped to `[-maxYears, maxYears]`.
 */
export const yearOffset = (
    anniversary: Date,
    instant: Date,
    maxYears: number,
): number => {
    if (instant.getTime() < anniversary.getTime()) {
        let offset = 0;
        while (offset > -maxYears) {
            const start = addUtcYears(anniversary, offset - 1);
            if (instant.getTime() >= start.getTime()) {
                return offset;
            }
            offset -= 1;
        }
        return -maxYears;
    }

    let offset = 1;
    while (offset < maxYears) {
        const end = addUtcYears(anniversary, offset);
        if (instant.getTime() < end.getTime()) {
            return offset;
        }
        offset += 1;
    }
    return maxYears;
};

export const getMidnightUTCDate = (date: Date): Date => {
    // Get the local timezone offset in minutes and convert it to milliseconds
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    // Subtract the offset from the UTC date to get the local date
    const dateMinusOffset = new Date(date.getTime() - timezoneOffset);
    const dateWithoutTimezone = formatWithoutTimezone(dateMinusOffset);
    return isoDateToUTCTimezoneDate(dateWithoutTimezone);
};

export const getCurrentMonthName = () =>
    new Date().toLocaleString('default', { month: 'long' });

/**
 * Converts a 24-hour "HH:MM:SS" time string to a display-friendly
 * format like "11:00 AM ET" or "6:30 AM ET".
 */
export const formatExecutionTime = (time24: string): string => {
    const [hourStr, minuteStr] = time24.split(':');
    let hour = parseInt(hourStr ?? '0', 10);
    const minute = parseInt(minuteStr ?? '0', 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;
    const minutePart =
        minute > 0 ? `:${minute.toString().padStart(2, '0')}` : ':00';
    return `${hour}${minutePart} ${ampm} ET`;
};

const MINIMUM_AGE = 18;
const MAXIMUM_AGE = 120;

export const zodDateOfBirthValidation = z
    .string()
    .regex(DEFAULT_DATE_REGEX)
    .refine((dob) => diffInYearsFromNow(dob) >= MINIMUM_AGE, {
        message: 'You must be over 18',
    })
    .refine((dob) => diffInYearsFromNow(dob) <= MAXIMUM_AGE, {
        message: 'Please enter a valid year',
    });
